import { Flex, Select, Switch } from "@jsq/ds/core";
import { SPACING } from "~/styles/variables";
import {
  DATE_ORDER_OPTIONS,
  DATE_SEPARATOR_OPTIONS,
  DAY_TOKEN_OPTIONS,
  MONTH_TOKEN_OPTIONS,
  WEEKDAY_TOKEN_OPTIONS,
  YEAR_TOKEN_OPTIONS,
} from "./constants";
import { getOrderParts, supportsWeekday } from "./patternBuilder";
import * as styles from "./styles_vanilla.css";
import type {
  DateFormatBuilderState,
  DateOrderOption,
  DatePart,
  DateSeparatorOption,
  DayToken,
  MonthToken,
  WeekdayToken,
  YearToken,
} from "./patternBuilder";
import type { SelectOption } from "./types";

type BuilderSelectProps<T extends string> = {
  label: string;
  hideLabel?: boolean;
  value: string;
  options: ReadonlyArray<SelectOption<T>>;
  onChange: (value: T) => void;
  disabled: boolean;
  className?: string;
  testId?: string;
};

const BuilderSelect = <T extends string>({
  label,
  hideLabel,
  value,
  options,
  onChange,
  disabled,
  className,
  testId,
}: BuilderSelectProps<T>) => (
  <Select
    label={label}
    hideLabel={hideLabel}
    value={value}
    className={className ?? styles.formControl}
    onChange={(next) => onChange(next as T)}
    disabled={disabled}
    data-testid={testId}
  >
    <Select.Section>
      {options.map((opt) => (
        <Select.Option key={opt.value} value={opt.value}>
          {opt.label}
        </Select.Option>
      ))}
    </Select.Section>
  </Select>
);

const PART_LABELS: Record<DatePart, string> = {
  month: "Month display",
  day: "Day display",
  year: "Year display",
};

const partSelectProps = (
  part: DatePart,
  state: DateFormatBuilderState,
  onChange: (state: DateFormatBuilderState) => void,
): {
  value: string;
  options: ReadonlyArray<SelectOption<string>>;
  set: (v: string) => void;
} => {
  switch (part) {
    case "month":
      return {
        value: state.monthToken,
        options: MONTH_TOKEN_OPTIONS,
        set: (v) => onChange({ ...state, monthToken: v as MonthToken }),
      };
    case "day":
      return {
        value: state.dayToken,
        options: DAY_TOKEN_OPTIONS,
        set: (v) => onChange({ ...state, dayToken: v as DayToken }),
      };
    case "year":
      return {
        value: state.yearToken,
        options: YEAR_TOKEN_OPTIONS,
        set: (v) => onChange({ ...state, yearToken: v as YearToken }),
      };
  }
};

type DateFormatBuilderFieldsProps = {
  state: DateFormatBuilderState;
  onChange: (state: DateFormatBuilderState) => void;
  disabled?: boolean;
  /** Uniquifies the weekday switch id when several builders coexist. */
  idPrefix: string;
};

/**
 * Composable date-format controls: order select, weekday toggle
 * (+ conditional weekday display), 1–3 "Date display" part selects
 * mirroring the chosen order, and a separator select.
 *
 * Presentational: consumes builder state and reports the next state.
 * `OnState` wires it to the `dateFormatPattern` formik field via
 * `patternBuilder`; `OffState` renders it disabled from the inherited
 * pattern.
 */
export const DateFormatBuilderFields = ({
  state,
  onChange,
  disabled = false,
  idPrefix,
}: DateFormatBuilderFieldsProps) => {
  const parts = getOrderParts(state.order);
  const weekdayVisible = supportsWeekday(state.order);

  return (
    <Flex flexDirection="column" gap={SPACING.MEDIUM}>
      <BuilderSelect
        label="Date format"
        value={state.order}
        options={DATE_ORDER_OPTIONS}
        onChange={(order: DateOrderOption) => onChange({ ...state, order })}
        disabled={disabled}
        testId="date-format-order"
      />
      <Flex alignItems="flex-end" gap={SPACING.SMALL} className={styles.formControl}>
        {parts.map((part, index) => {
          const { value, options, set } = partSelectProps(part, state, onChange);
          return (
            <BuilderSelect
              key={part}
              label={index === 0 ? "Date display" : PART_LABELS[part]}
              hideLabel={index > 0}
              value={value}
              options={options}
              onChange={set}
              disabled={disabled}
              className={styles.datePartControl}
              testId={`date-format-part-${part}`}
            />
          );
        })}
      </Flex>
      {/* Single-part orders (Year only) have nothing to separate, so the
          control is hidden entirely (per ticket). */}
      {parts.length > 1 && (
        <BuilderSelect
          label="Date separators"
          value={state.separator}
          options={DATE_SEPARATOR_OPTIONS}
          onChange={(separator: DateSeparatorOption) => onChange({ ...state, separator })}
          disabled={disabled}
          testId="date-format-separator"
        />
      )}
      {weekdayVisible && (
        <Switch
          id={`${idPrefix}-date-format-weekday`}
          label="Weekday"
          labelPosition="right"
          size="small"
          checked={state.weekdayEnabled}
          onChange={(event) => onChange({ ...state, weekdayEnabled: event.target.checked })}
          disabled={disabled}
        />
      )}
      {weekdayVisible && state.weekdayEnabled && (
        <BuilderSelect
          label="Weekday display"
          value={state.weekdayToken}
          options={WEEKDAY_TOKEN_OPTIONS}
          onChange={(weekdayToken: WeekdayToken) => onChange({ ...state, weekdayToken })}
          disabled={disabled}
          testId="date-format-weekday-display"
        />
      )}
    </Flex>
  );
};
